import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 30000, // 30 second timeout to prevent infinite hangs
});

// In-memory access token (not in localStorage for security)
let accessToken: string | null = null;

// Proactive refresh timer
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

// Callback for external listeners (e.g., socket reconnect) after token refresh
let onTokenRefreshCallback: (() => void) | null = null;

/** Register a callback that fires after every successful token refresh */
export function onTokenRefresh(cb: () => void): void {
  onTokenRefreshCallback = cb;
}

// How many minutes before expiry to proactively refresh (access token is 1 hour)
const PROACTIVE_REFRESH_MS = 55 * 60 * 1000; // 55 minutes — 5 min before 1-hour expiry

// Token management functions
export function getAccessToken(): string | null {
  return accessToken;
}

let lastRefreshTime = Date.now();

export function setTokens(newAccessToken: string): void {
  accessToken = newAccessToken;
  lastRefreshTime = Date.now();
  scheduleProactiveRefresh();
  onTokenRefreshCallback?.();
}

export function clearTokens(): void {
  accessToken = null;
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

export function isAuthenticated(): boolean {
  return !!accessToken;
}

/** Schedule a silent token refresh before the access token expires */
function scheduleProactiveRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }
  refreshTimer = setTimeout(async () => {
    try {
      await refreshAccessToken();
    } catch {
      // Proactive refresh failed — will fall back to reactive 401 refresh
    }
  }, PROACTIVE_REFRESH_MS);
}

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let refreshSubscribers: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = [];

function subscribeTokenRefresh(resolve: (token: string) => void, reject: (err: unknown) => void) {
  refreshSubscribers.push({ resolve, reject });
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((s) => s.resolve(token));
  refreshSubscribers = [];
}

function onRefreshFailed(err: unknown) {
  refreshSubscribers.forEach((s) => s.reject(err));
  refreshSubscribers = [];
}

/**
 * Attempt to refresh the access token using the httpOnly refresh cookie.
 * Returns the new access token, or throws if refresh fails.
 * Deduplicates concurrent calls — only one refresh request is in-flight at a time.
 */
export async function refreshAccessToken(): Promise<string> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/refresh`, {}, {
        withCredentials: true,
      });
      const { accessToken: newAccessToken } = response.data;
      setTokens(newAccessToken);
      onTokenRefreshed(newAccessToken);
      return newAccessToken;
    } catch (err) {
      onRefreshFailed(err);
      // Only clear tokens on definitive auth failure (401/403 from refresh endpoint).
      // Network errors or 5xx should NOT log the user out — they can retry.
      if (axios.isAxiosError(err) && err.response && (err.response.status === 401 || err.response.status === 403)) {
        clearTokens();
      }
      throw err;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Request interceptor - add auth token and handle FormData Content-Type
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Let the browser set Content-Type with boundary for FormData
    if (config.data instanceof FormData) {
      config.headers.delete('Content-Type');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 and refresh token
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If error is not 401 or request already retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Don't try to refresh for refresh/login/register endpoints (prevents infinite loop)
    const url = originalRequest.url || '';
    if (url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/register')) {
      return Promise.reject(error);
    }

    // If we're already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh(
          (token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(apiClient(originalRequest));
          },
          reject
        );
      });
    }

    originalRequest._retry = true;

    try {
      const newAccessToken = await refreshAccessToken();

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      }

      return apiClient(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

// Refresh token when tab wakes up after being idle
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && accessToken) {
    const elapsed = Date.now() - lastRefreshTime;
    if (elapsed > 50 * 60 * 1000) { // 50 minutes
      refreshAccessToken().catch(() => {});
    }
  }
});

// API response types
export interface ApiError {
  error: string;
  message?: string;
}

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error);
}

/**
 * Unwrap the underlying transport error.
 *
 * Services that go through `lib/api` (apiGet/apiPost) throw a normalized
 * AppError rather than the axios error, keeping the original under
 * `originalError`. Without this hop, every one of those callers fell through to
 * "An unexpected error occurred" and the server's actual message — including
 * plan-limit messages like "Your plan includes 2 child profiles" — was never
 * shown to anyone.
 */
function unwrapError(error: unknown): unknown {
  let current = error;
  // Bounded: normalizeError nests at most one level, but guard against a cycle.
  for (let i = 0; i < 5; i++) {
    if (isApiError(current)) return current;
    const inner = (current as { originalError?: unknown } | null)?.originalError;
    if (!inner || inner === current) return current;
    current = inner;
  }
  return current;
}

export function getErrorMessage(error: unknown): string {
  const unwrapped = unwrapError(error);
  if (isApiError(unwrapped)) {
    return unwrapped.response?.data?.error || unwrapped.message || 'An error occurred';
  }
  // Normalized AppError carries a human-readable userMessage.
  const userMessage = (error as { userMessage?: unknown } | null)?.userMessage;
  if (typeof userMessage === 'string' && userMessage) return userMessage;
  if (unwrapped instanceof Error) return unwrapped.message;
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
}

/** The raw axios error behind any thrown value, or null. */
export function asApiError(error: unknown): AxiosError<ApiError> | null {
  const unwrapped = unwrapError(error);
  return isApiError(unwrapped) ? unwrapped : null;
}

/**
 * Get the full URL for an uploaded file.
 * Handles three formats:
 *   - Full URL (http/https) → returned as-is (e.g., S3 signed URLs)
 *   - Absolute path (/uploads/...) → prepends backend base URL
 *   - Bare storage key (child-avatars/...) → prepends /uploads/ and backend base URL
 */
export function getUploadUrl(relativePath: string | null | undefined): string | undefined {
  if (!relativePath) return undefined;

  // If already a full URL, return as-is
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    return relativePath;
  }

  // Get the base URL (API_URL without /api suffix)
  const baseUrl = API_URL.replace(/\/api\/?$/, '');

  // If it's already an absolute path (starts with /), use as-is
  // Otherwise it's a bare storage key — prepend /uploads/
  const path = relativePath.startsWith('/')
    ? relativePath
    : `/uploads/${relativePath}`;

  return `${baseUrl}${path}`;
}
